"use client";

import React, { useEffect, useRef, useState } from "react";
import AppImage from "@/components/ui/AppImage";

const testimonials = [
{
  quote: "I used to start every morning drowning in 200+ emails. Now I wake up to a brief that already has 80% of my replies drafted. The Ghostwriter nails my tone — my EA thought I wrote them myself.",
  author: "Marcus Chen",
  role: "CEO",
  company: "Axiom Ventures",
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_183fd5c93-1763300382593.png",
  altText: "Marcus Chen, CEO of Axiom Ventures, smiling professional headshot",
  rating: 5,
  highlight: "80% of replies drafted",
  highlightColor: "#F59E0B"
},
{
  quote: "The Airlock engine is magic. I haven't manually declined a meeting request in 6 weeks. It handles the back-and-forth, protects my deep work blocks, and books everything without a single notification.",
  author: "Priya Nair",
  role: "COO",
  company: "Meridian Health Systems",
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_12672b149-1763294392419.png",
  altText: "Priya Nair, COO of Meridian Health Systems, professional headshot with warm smile",
  rating: 5,
  highlight: "6 weeks, zero manual declines",
  highlightColor: "#818CF8"
},
{
  quote: "The 6 AM brief changed how I think about my day. I used to check my phone immediately when I woke up — anxious. Now I read a curated summary that tells me exactly what needs me and what doesn't.",
  author: "David Okafor",
  role: "Founder & CTO",
  company: "Stackflow Labs",
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1fc20034d-1763295634141.png",
  altText: "David Okafor, Founder and CTO of Stackflow Labs, confident professional headshot",
  rating: 5,
  highlight: "Changed how I think about my day",
  highlightColor: "#22C55E"
},
{
  quote: "We rolled it out to our 12-person exec team. Scheduling conflicts dropped by 64% in the first month. The Airlock engine negotiates between team calendars automatically — it\'s like having a full-time scheduler for every exec.",
  author: "Sophie Beaumont",
  role: "Chief of Staff",
  company: "Northgate Capital Partners",
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_12672b149-1763294392419.png",
  altText: "Sophie Beaumont, Chief of Staff at Northgate Capital Partners, professional business headshot",
  rating: 5,
  highlight: "64% fewer scheduling conflicts",
  highlightColor: "#F59E0B"
},
{
  quote: "I was skeptical about AI writing my emails. Ghostwriter\'s relationship memory changed my mind. It remembered that I always start calls with Raj with a cricket reference — and the draft had one. I nearly fell out of my chair.",
  author: "Arjun Sharma",
  role: "VP Partnerships",
  company: "Zenith Global",
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_179ebd6f2-1763294255544.png",
  altText: "Arjun Sharma, VP Partnerships at Zenith Global, smiling professional headshot",
  rating: 5,
  highlight: "Relationship memory is uncanny",
  highlightColor: "#818CF8"
}];


const StarRating: React.FC<{count: number;}> = ({ count }) =>
<div className="flex gap-0.5">
    {Array.from({ length: count }).map((_, i) =>
  <svg key={i} width="14" height="14" viewBox="0 0 24 24" fill="#F59E0B">
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
      </svg>
  )}
  </div>;


const TestimonialsSection: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {if (entry.isIntersecting) setVisible(true);},
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  };

  const scroll = (dir: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir === "left" ? -400 : 400, behavior: "smooth" });
  };

  return (
    <section
      id="testimonials"
      ref={sectionRef}
      className="relative py-32 overflow-hidden"
      style={{ background: "#0D0F14" }}>
      
      {/* Background */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: "radial-gradient(ellipse 80% 40% at 50% 100%, rgba(245,158,11,0.05) 0%, transparent 70%)"
        }} />
      

      <div className="max-w-6xl mx-auto px-6 relative z-10">
        {/* Section divider */}
        <div className="section-divider mb-20" />

        {/* Header */}
        <div
          className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0)" : "translateY(24px)",
            transition: "all 0.8s cubic-bezier(0.16, 1, 0.3, 1)"
          }}>
          
          <div>
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4 text-xs font-semibold tracking-widest uppercase"
              style={{ background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.15)", color: "#F59E0B" }}>
              
              Early Access Voices
            </div>
            <h2
              className="font-display leading-tight"
              style={{ fontSize: "clamp(32px, 5vw, 52px)", color: "#F0EDE8", letterSpacing: "-0.02em" }}>
              
              Executives who{" "}
              <span className="font-display italic" style={{ color: "#9B9891" }}>stopped drowning.</span>
            </h2>
          </div>

          {/* Navigation */}
          <div className="flex gap-2 shrink-0">
            <button
              onClick={() => scroll("left")}
              disabled={!canScrollLeft}
              className="w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-200"
              style={{
                background: canScrollLeft ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.02)",
                border: `1px solid ${canScrollLeft ? "rgba(255,255,255,0.12)" : "rgba(255,255,255,0.04)"}`,
                color: canScrollLeft ? "#9B9891" : "#3A3A3A"
              }}
              aria-label="Scroll left">
              
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
            <button
              onClick={() => scroll("right")}
              disabled={!canScrollRight}
              className="w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-200"
              style={{
                background: canScrollRight ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.02)",
                border: `1px solid ${canScrollRight ? "rgba(255,255,255,0.12)" : "rgba(255,255,255,0.04)"}`,
                color: canScrollRight ? "#9B9891" : "#3A3A3A"
              }}
              aria-label="Scroll right">
              
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Scrollable testimonials */}
      <div
        ref={scrollRef}
        onScroll={checkScroll}
        className="scrollbar-hide flex gap-5 overflow-x-auto pb-4"
        style={{ paddingLeft: "max(24px, calc((100vw - 1200px) / 2))", paddingRight: "24px" }}>
        
        {testimonials.map((t, idx) =>
        <div
          key={`${t.author}-${idx}`}
          className="shrink-0 w-80 md:w-96 rounded-3xl p-7 flex flex-col gap-5 transition-all duration-500 group"
          style={{
            background: "linear-gradient(145deg, #1A1D26 0%, #13151C 100%)",
            border: "1px solid rgba(255,255,255,0.06)",
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0)" : "translateY(24px)",
            transition: `all 0.8s cubic-bezier(0.16, 1, 0.3, 1) ${idx * 0.08}s`
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLElement).style.borderColor = `${t.highlightColor}25`;
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLElement).style.borderColor = "rgba(255,255,255,0.06)";
          }}>
          
            {/* Rating */}
            <StarRating count={t.rating} />

            {/* Highlight badge */}
            <div
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold w-fit"
            style={{ background: `${t.highlightColor}12`, color: t.highlightColor }}>
            
              <div className="w-1.5 h-1.5 rounded-full" style={{ background: t.highlightColor }} />
              {t.highlight}
            </div>

            {/* Quote */}
            <blockquote className="text-sm leading-relaxed flex-1" style={{ color: "#9B9891" }}>
              "{t.quote}"
            </blockquote>

            {/* Author */}
            <div
            className="flex items-center gap-3 pt-4"
            style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
            
              <div className="w-10 h-10 rounded-full overflow-hidden shrink-0">
                <AppImage
                src={t.avatar}
                alt={t.altText}
                width={40}
                height={40}
                className="w-full h-full object-cover" />
              
              </div>
              <div>
                <div className="text-sm font-semibold" style={{ color: "#F0EDE8" }}>{t.author}</div>
                <div className="text-xs" style={{ color: "#5C5A56" }}>{t.role}, {t.company}</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Aggregate stats */}
      <div className="max-w-6xl mx-auto px-6 mt-16">
        <div
          className="grid grid-cols-2 md:grid-cols-4 gap-6 rounded-3xl p-8"
          style={{
            background: "linear-gradient(145deg, #1A1D26 0%, #13151C 100%)",
            border: "1px solid rgba(255,255,255,0.06)",
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0)" : "translateY(24px)",
            transition: "all 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.5s"
          }}>
          
          {[
          { value: "4.9/5", label: "Average rating", icon: "⭐" },
          { value: "2,400+", label: "Early access users", icon: "👥" },
          { value: "3.2 hrs", label: "Daily time reclaimed", icon: "⏱️" },
          { value: "78%", label: "Emails handled autonomously", icon: "✅" }].
          map((stat, i) =>
          <div key={i} className="text-center">
              <div className="text-2xl mb-2">{stat.icon}</div>
              <div
              className="font-display font-semibold text-2xl mb-1"
              style={{ color: "#F59E0B", letterSpacing: "-0.02em" }}>
              
                {stat.value}
              </div>
              <div className="text-xs" style={{ color: "#5C5A56" }}>{stat.label}</div>
            </div>
          )}
        </div>
      </div>
    </section>);

};

export default TestimonialsSection;